import React, { lazy, Suspense, useEffect } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { Skeleton } from 'antd';
import useStores from '@stores/useStores';
import isFeatureEnabled, { Feature } from '@utils/enableFeature';
import { canAccessRoute, isConnectionAvialable } from './utils';
import { StretchLayout } from './styles';

const Connections = lazy(() => import('@components/connections'));
const Canny = lazy(() => import('@auth/canny'));
const GetStarted = lazy(() => import('@components/quickstart'));
const DestinationDetails = lazy(() => import('@components/destinations/destination-view'));
const Destinations = lazy(() => import('@components/destinations/destination-list'));
const MetricsPage = lazy(() => import('@components/metrics'));
const TransformationsPage = lazy(() => import('@components/transformations'));
const TransformationDetails = lazy(
  () => import('@components/transformations/transformationDetails/transformationDetails'),
);
const AddTransformation = lazy(() => import('@components/transformations/addTranformation'));
const LibraryDetails = lazy(() => import('@components/transformations/libraries/libraryDetails'));
const RudderStackLibraryDetails = lazy(
  () => import('@components/transformations/rudderstackLibraries/libraryDetails'),
);
const AddLibrary = lazy(() => import('@components/transformations/libraries/addLibrary'));
const Directory = lazy(() => import('@components/directory'));
const Syncs = lazy(() => import('@components/syncs'));
const Profile = lazy(() => import('@components/profile'));
const StatusesView = lazy(() => import('@components/destinations/liveEvents'));
const EventsView = lazy(() => import('@components/sources/liveEvents'));
const IntroPage = lazy(() => import('@components/common/liveEvents/introPage'));
const EventTransformStatusesView = lazy(() => import('@components/transformations/liveEvents'));
const SyncDetail = lazy(() => import('@components/syncs/syncDetail'));
const CloneDestination = lazy(() => import('@components/workflows/destination/clone/clone'));
const SourceViewComponent = lazy(() => import('@components/sources/source-view'));
const SourcesListComponent = lazy(() => import('@components/sources/source-list'));
const Models = lazy(() => import('@components/models/modelsList'));
const AddModel = lazy(() => import('@components/models/addModel'));
const ModelView = lazy(() => import('@components/models/modelView'));
const CreateDestination = lazy(() => import('@components/workflows/destination/create'));
const ConnectNewSource = lazy(() => import('@components/workflows/destination/connectNewSource'));
const CreateSource = lazy(() => import('@components/workflows/source/create'));
const ConnectExistingSource = lazy(
  () => import('@components/workflows/destination/connectExistingSource'),
);
const ConnectExistingDestination = lazy(
  () => import('@components/workflows/source/connectExistingDestination'),
);
const ConnectNewDestination = lazy(
  () => import('@components/workflows/source/connectNewDestination'),
);
const TrackingPlanListComponent = lazy(() => import('@components/trackingPlans/trackingPlanList'));
const TrackingPlanViewComponent = lazy(() => import('@components/trackingPlans/trackingPlanView'));
const LinkSourceComponent = lazy(() => import('@components/trackingPlans/linkSource'));
const LinkTrackingPlanComponent = lazy(() => import('@components/trackingPlans/linkTrackingPlan'));
const AddAudience = lazy(() => import('@components/warehouse/audiences/addAudience'));
const Audiences = lazy(() => import('@components/warehouse/audiences/audiencesList'));
const AudienceView = lazy(() => import('@components/warehouse/audiences/audienceView'));
const WHTProjects = lazy(() => import('@components/whtProjects/whtProjectsList'));

export const Path = {
  ADD_AUDIENCE: '/addAudience',
  CREATE_DESTINATION: '/addDestination',
  CONNECT_EXISTING_SOURCE: (id: string) => `/destinations/${id}/connect`,
  CONNECT_EXISTING_DESTINATION: (id: string) => `/sources/${id}/connect`,
  CONNECT_NEW_DESTINATION: (id: string) => `/sources/${id}/connectNewDestination`,
  CREATE_SOURCE: '/addSource',
  CONNECT_NEW_SOURCE: (destinationId: string) => `/destinations/${destinationId}/connectNewSource`,
  CLONE_DESTINATION: (destinationId: string) => `/destinations/${destinationId}/clone`,
  HOME: '/',
  GET_STARTED: '/getStarted',
  SETTINGS: '/profile',
  SOURCES: '/sources',
  SOURCE_DETAILS: (id = ':id') => `${Path.SOURCES}/${id}`,
  DESTINATIONS: '/destinations',
  DESTINATION_DETAILS: (id = ':id') => `${Path.DESTINATIONS}/${id}`,
  SYNCS: '/syncs',
  MODELS: '/models',
  AUDIENCES: '/audiences',
  AUDIENCE_DETAILS: (id = ':id') => `${Path.AUDIENCES}/${id}`,
  WHT_PROJECTS: '/whtProjects',
  WHT_PROJECT_SETUP: '/whtProjects/setup',
  WHT_PROJECT_OPEN: (id: string) => `/whtProjects/${id}`,
  DIRECTORY: '/directory',
  TRANSFORMATIONS: '/transformations',
  ADD_TRANSFORMATION: '/transformations/add',
  TRACKING_PLANS: '/trackingPlans',
  TRACKING_PLAN_DETAILS: (id: string) => `/trackingPlans/${id}`,
  LINK_NEW_SOURCE: (id: string) => `/trackingplans/${id}/linkSource`,
  LINK_TRACKING_PLAN: (id: string) => `/sources/${id}/linkTrackingPlan`,
  LOGIN: '/login',
  SIGNUP: '/signup',
  CANNY: '/canny',
  FORGOT_PASSWORD: '/forgotPassword',
};

const Routes = ({ collapsed }: { collapsed: boolean }) => {
  const history = useHistory();
  const location = useLocation<{ isSideMenuClick?: boolean }>();

  const {
    messagesStore,
    permissionsStore: { hasPermission, hasDataPermission },
    connectionsStore,
  } = useStores();

  useEffect(
    () =>
      history.listen(() => {
        messagesStore.setError(false);
        window.rudderanalytics.page('Page viewed', 'Page viewed', {
          path: location.pathname,
          referrer: '',
          search: '',
          title: '',
          url: window.location.href,
        });
      }),
    [location],
  );

  const isEnabled = isFeatureEnabled();

  const enableGrafana = isEnabled(Feature.grafana);
  const enableWHTProjects = isEnabled(Feature.whtProjects);
  const enableSqlModel = isEnabled(Feature.models);
  const enableAudience = isEnabled(Feature.audiences);
  const enableTrackingPlans = isEnabled(Feature.trackingPlans);
  const enableQuickstart = isEnabled(Feature.quickstart);

  const renderQuickstart =
    !isConnectionAvialable(connectionsStore.connections) &&
    enableQuickstart &&
    !location.state?.isSideMenuClick;
  if (!canAccessRoute(location.pathname, hasPermission, hasDataPermission())) {
    history.push(Path.HOME);
    return null;
  }

  return (
    <StretchLayout>
      <Suspense
        fallback={
          <Skeleton>
            <h1>This is loading screen</h1>
          </Skeleton>
        }
      >
        <Switch>
          <Route
            path={Path.HOME}
            exact
            component={() =>
              renderQuickstart ? <GetStarted /> : <Connections collapsed={collapsed} />
            }
          />
          {enableQuickstart && <Route path={Path.GET_STARTED} exact component={GetStarted} />}
          {enableGrafana && <Route path="/metrics" exact component={MetricsPage} />}

          {/* Directory */}
          <Route path={Path.DIRECTORY} exact component={Directory} />

          {/* Profile */}
          <Route path={Path.SETTINGS} exact component={Profile} />
          {/* Sources */}
          <Route path={Path.SOURCES} exact component={SourcesListComponent} />
          <Route path={Path.CREATE_SOURCE} exact component={CreateSource} />
          <Route path={Path.CANNY} exact component={Canny} />

          <Route path="/sources/live/intro/:id" exact component={IntroPage} />
          <Route path="/sources/live/:id" exact component={EventsView} />
          <Route
            path={Path.CONNECT_EXISTING_DESTINATION(':id')}
            exact
            component={ConnectExistingDestination}
          />
          <Route
            path={Path.CONNECT_NEW_DESTINATION(':id')}
            exact
            component={ConnectNewDestination}
          />
          <Route path={Path.SOURCE_DETAILS()} exact component={SourceViewComponent} />

          {/* Destinations */}
          <Route path={Path.DESTINATIONS} exact component={Destinations} />
          <Route path={Path.CLONE_DESTINATION(':id')} exact component={CloneDestination} />
          <Route path="/destinations/live/intro/:id" exact component={IntroPage} />
          <Route path="/destinations/live/:id" exact component={StatusesView} />
          <Route path={Path.DESTINATION_DETAILS()} exact component={DestinationDetails} />
          <Route
            path={Path.CONNECT_NEW_SOURCE(':destinationId')}
            exact
            component={ConnectNewSource}
          />
          <Route path={Path.CREATE_DESTINATION} exact component={CreateDestination} />
          <Route
            path={Path.CONNECT_EXISTING_SOURCE(':id')}
            exact
            component={ConnectExistingSource}
          />

          {/* Transformations */}
          <Route path={Path.TRANSFORMATIONS} exact component={TransformationsPage} />
          <Route path={Path.ADD_TRANSFORMATION} exact component={AddTransformation} />
          <Route path="/transformations/live/intro/:id" exact component={IntroPage} />
          <Route path="/transformations/live/:id" exact component={EventTransformStatusesView} />
          <Route path="/transformations/:id" exact component={TransformationDetails} />
          <Route path="/libraries/add" exact component={AddLibrary} />
          <Route path="/libraries/:id" exact component={LibraryDetails} />
          <Route path="/rudderstackLibraries/:id" exact component={RudderStackLibraryDetails} />

          {/* WHT Projects */}
          {enableWHTProjects && <Route path={Path.WHT_PROJECTS} exact component={WHTProjects} />}

          {/* Models */}
          {enableSqlModel && <Route path="/models" exact component={Models} />}
          {enableSqlModel && <Route path="/models/setup" exact component={AddModel} />}
          {enableSqlModel && <Route path="/models/:id" exact component={ModelView} />}

          {/* Audiences */}
          {enableAudience && <Route path={Path.ADD_AUDIENCE} exact component={AddAudience} />}
          {enableAudience && <Route path={Path.AUDIENCES} exact component={Audiences} />}
          {enableAudience && (
            <Route path={Path.AUDIENCE_DETAILS()} exact component={AudienceView} />
          )}

          {/* Syncs */}
          <Route path={Path.SYNCS} exact component={Syncs} />
          <Route path="/syncs/:id" exact component={SyncDetail} />

          {/* Tracking plans */}
          {enableTrackingPlans && (
            <Route path={Path.TRACKING_PLANS} exact component={TrackingPlanListComponent} />
          )}
          {enableTrackingPlans && (
            <Route
              path={Path.TRACKING_PLAN_DETAILS(':id')}
              exact
              component={TrackingPlanViewComponent}
            />
          )}
          {enableTrackingPlans && (
            <Route path={Path.LINK_NEW_SOURCE(':id')} exact component={LinkSourceComponent} />
          )}
          {enableTrackingPlans && (
            <Route
              path={Path.LINK_TRACKING_PLAN(':id')}
              exact
              component={LinkTrackingPlanComponent}
            />
          )}

          {/* Wild card route - should be at end always  */}
          <Route path="*" render={() => <Redirect to="/" />} />
        </Switch>
      </Suspense>
    </StretchLayout>
  );
};
export default Routes;
